import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Device } from '@capacitor/device';
import { apiRequest } from '@/lib/queryClient';

// Custom events for push notification token and errors
export interface PushNotificationToken {
  token: string;
  platform: string;
}

export interface PushTokenEventDetail {
  token: string;
  platform: string;
}

export interface PushErrorEventDetail {
  error: Error;
}

export interface PushTokenEvent extends Event {
  detail?: PushTokenEventDetail;
}

export interface PushErrorEvent extends Event {
  detail?: PushErrorEventDetail;
}

class PushNotificationService {
  private initialized: boolean = false;
  private registrationInProgress: boolean = false;
  private retryCount: number = 0;
  private maxRetries: number = 5;
  private initialRetryDelay: number = 1000; // 1 second
  private deviceInfo: any = null;
  
  /**
   * Initialize push notifications
   * This should be called when the user logs in
   */
  async initialize(): Promise<void> {
    // Skip if not on a mobile device or already initialized
    if (!Capacitor.isNativePlatform() || this.initialized) {
      return;
    }

    try {
      // Request permission and register for push notifications
      const permissionStatus = await PushNotifications.requestPermissions();
      
      if (permissionStatus.receive === 'granted') {
        // Register with device
        await PushNotifications.register();
        this.initialized = true;
        
        // Get device info for token registration
        this.deviceInfo = await Device.getInfo();
        
        // Listen for token events
        PushNotifications.addListener('registration', (token) => {
          this.handlePushToken(token.value);
        });
        
        // Listen for push notification received events
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('Push notification received:', notification);
          // Handle displaying the notification when app is in foreground if needed
        });
        
        // Listen for notification clicked events
        PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          console.log('Push notification action performed:', notification);
          // Handle navigation or other actions when a notification is clicked
        });
        
        // Listen for notification error events
        PushNotifications.addListener('registrationError', (error) => {
          this.handlePushError(error);
        });
      }
    } catch (error) {
      this.handlePushError(error as Error);
    }
  }
  
  /**
   * Handle push token registration with exponential backoff
   * @param token Push notification token
   */
  private async handlePushToken(token: string): Promise<void> {
    try {
      if (this.registrationInProgress) return;
      
      this.registrationInProgress = true;
      const platform = this.deviceInfo?.platform || 'unknown';
      const deviceName = this.deviceInfo?.name || this.deviceInfo?.model || 'unknown device';
      
      // Dispatch token event for other components to use
      const event = new CustomEvent('push_token', { 
        detail: { 
          token, 
          platform,
        } 
      });
      window.dispatchEvent(event);
      
      // Register token with backend
      await this.registerTokenWithBackend(token, platform, deviceName);
      
      // Reset retry count after successful registration
      this.retryCount = 0;
      this.registrationInProgress = false;
    } catch (error) {
      this.registrationInProgress = false;
      await this.retryTokenRegistration(token);
    }
  }
  
  /**
   * Retry token registration with exponential backoff
   * @param token Push notification token
   */
  private async retryTokenRegistration(token: string): Promise<void> {
    if (this.retryCount >= this.maxRetries) {
      console.error('Max retry attempts reached for push token registration');
      return;
    }
    
    // Calculate delay with exponential backoff and some jitter
    const delay = this.initialRetryDelay * Math.pow(2, this.retryCount) + 
                  Math.random() * 1000; // Add up to 1 second of random jitter
    
    this.retryCount++;
    console.log(`Retrying push token registration (attempt ${this.retryCount}) in ${Math.round(delay)}ms`);
    
    // Wait before retrying
    await new Promise(resolve => setTimeout(resolve, delay));
    
    try {
      if (this.deviceInfo) {
        const platform = this.deviceInfo.platform || 'unknown';
        const deviceName = this.deviceInfo.name || this.deviceInfo.model || 'unknown device';
        await this.registerTokenWithBackend(token, platform, deviceName);
        this.retryCount = 0; // Reset on success
      } else {
        throw new Error('Device info not available');
      }
    } catch (error) {
      await this.retryTokenRegistration(token);
    }
  }
  
  /**
   * Register token with backend API
   */
  private async registerTokenWithBackend(token: string, platform: string, deviceName: string): Promise<void> {
    const response = await apiRequest('POST', '/api/device-tokens', {
      token,
      platform,
      deviceName
    });
    
    if (!response.ok) {
      throw new Error('Failed to register push token with backend');
    }
  }
  
  /**
   * Handle push notification errors
   * @param error Error object
   */
  private handlePushError(error: Error): void {
    console.error('Push notification error:', error);
    
    // Dispatch error event for other components to use
    const event = new CustomEvent('push_error', { 
      detail: { error } 
    });
    window.dispatchEvent(event);
  }
  
  /**
   * Deactivate push token on logout
   * This should be called when the user logs out
   */
  async deactivateToken(): Promise<void> {
    try {
      // Skip if not on a mobile device or not initialized
      if (!Capacitor.isNativePlatform() || !this.initialized) {
        return;
      }
      
      // Get current token from device
      const pushToken = await this.getCurrentPushToken();
      
      if (pushToken) {
        // Call API to deactivate token
        const response = await apiRequest('POST', '/api/device-tokens/deactivate', {
          token: pushToken
        });
        
        if (!response.ok) {
          throw new Error('Failed to deactivate push token');
        }
      }
      
      // Reset initialized flag
      this.initialized = false;
      
      // Remove all listeners
      PushNotifications.removeAllListeners();
      
    } catch (error) {
      console.error('Failed to deactivate push token:', error);
      throw error;
    }
  }
  
  /**
   * Get the current push token from the device
   * @returns Promise resolving to the current push token or null if not available
   */
  private async getCurrentPushToken(): Promise<string | null> {
    try {
      // This is a workaround as Capacitor doesn't provide a direct way to get the current token
      // In a real implementation, consider storing the token in a secure storage when received
      return null;
    } catch (error) {
      console.error('Failed to get current push token:', error);
      return null;
    }
  }
}

// Export singleton instance
const pushNotificationService = new PushNotificationService();
export default pushNotificationService;